import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useConfig } from './config'
import { useSession } from './session'

/**
 * Live prices from /api/v2/market/stream (Dhan WebSocket → server → SSE, ~1 update/s).
 * A module-level store so every price on screen shares ONE connection, and components
 * subscribe only to what they show.
 */
export interface LiveQuote {
  ltp: number; change: number | null; change_pct: number | null; high?: number | null; low?: number | null; volume?: number | null
  /** ms timestamp of the last real trade tick (absent for values from the initial snapshot). */
  ts?: number
}
export interface LiveRow extends LiveQuote { name: string }
type Row = [string, number, number | null, number | null, (number | null)?, (number | null)?, (number | null)?]
interface Batch { i?: Row[]; m?: Row[]; s?: Row[]; at?: string | null; live?: boolean }

const stocks = new Map<string, LiveQuote>()
let indices: LiveRow[] = []
let mcx: LiveRow[] = []
let status = { connected: false, at: 0 }
let version = 0
const listeners = new Set<() => void>()
const emit = () => { version++; listeners.forEach(l => l()) }
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }

const toQuote = (r: Row): LiveQuote => ({ ltp: r[1], change: r[2], change_pct: r[3], ...(r.length > 4 ? { high: r[4] ?? null, low: r[5] ?? null, volume: r[6] ?? null } : {}) })
function merge(list: LiveRow[], rows: Row[] | undefined, replace: boolean): LiveRow[] {
  if (!rows?.length) return replace ? [] : list
  const out = replace ? [] : [...list]
  for (const r of rows) {
    const row = { name: r[0], ...toQuote(r) }
    const i = out.findIndex(x => x.name === row.name)
    if (i >= 0) out[i] = row; else out.push(row)
  }
  return out
}
function apply(b: Batch, full: boolean) {
  if (full) stocks.clear()
  const now = Date.now()
  for (const r of b.s ?? []) stocks.set(r[0], { ...stocks.get(r[0]), ...toQuote(r), ...(full ? {} : { ts: now }) })
  if (b.i?.length || full) indices = merge(indices, b.i, full)
  if (b.m?.length || full) mcx = merge(mcx, b.m, full)
  status = { connected: true, at: Date.now() }
  emit()
}

// Extra symbols (stock pages) to add to the stream, ref-counted.
const extra = new Map<string, number>()
const extraListeners = new Set<() => void>()
const extraKey = () => [...extra.keys()].sort().join(',')

/** Mount once (AppShell): keeps the SSE connection while signed in and prices are on. */
export function LiveMarketConnection() {
  const cfg = useConfig()
  const { me } = useSession()
  const [symbols, setSymbols] = useState(extraKey())
  useEffect(() => {
    const l = () => setSymbols(extraKey())
    extraListeners.add(l)
    return () => { extraListeners.delete(l) }
  }, [])
  const on = cfg.prices_enabled && !!me
  useEffect(() => {
    if (!on || typeof EventSource === 'undefined') { status = { connected: false, at: status.at }; emit(); return }
    const es = new EventSource(`/api/v2/market/stream${symbols ? `?symbols=${encodeURIComponent(symbols)}` : ''}`, { withCredentials: true })
    const parse = (ev: Event) => { try { return JSON.parse((ev as MessageEvent<string>).data) as Batch } catch { return null } }
    es.addEventListener('snapshot', ev => { const b = parse(ev); if (b) apply(b, true) })
    es.addEventListener('ticks', ev => { const b = parse(ev); if (b) apply(b, false) })
    es.addEventListener('off', () => { es.close(); status = { connected: false, at: status.at }; emit() })
    es.onerror = () => { status = { ...status, connected: false }; emit() }
    return () => es.close()
  }, [on, symbols])
  return null
}

/** Ask the stream to include these symbols while the component is mounted (e.g. a stock page). */
export function useLiveSymbols(symbols: string[]) {
  const key = symbols.join(',')
  useEffect(() => {
    const list = key ? key.split(',') : []
    list.forEach(s => extra.set(s, (extra.get(s) ?? 0) + 1))
    extraListeners.forEach(l => l())
    return () => {
      list.forEach(s => { const n = (extra.get(s) ?? 1) - 1; if (n <= 0) extra.delete(s); else extra.set(s, n) })
      extraListeners.forEach(l => l())
    }
  }, [key])
}

/** Live quote for one stock; re-renders only when that stock ticks. */
export const useLiveQuote = (symbol: string | undefined): LiveQuote | undefined =>
  useSyncExternalStore(subscribe, () => (symbol ? stocks.get(symbol) : undefined))

/** Live index + MCX rows (for the ribbon). */
export function useLiveIndices(): { indices: LiveRow[]; mcx: LiveRow[] } {
  const i = useSyncExternalStore(subscribe, () => indices)
  const m = useSyncExternalStore(subscribe, () => mcx)
  return { indices: i, mcx: m }
}

/** Re-render on every batch (≈1/s) and read many symbols at once — for small lists (watchlist, movers, admin board). */
export function useLiveStocks(): (symbol: string) => LiveQuote | undefined {
  useSyncExternalStore(subscribe, () => version)
  return symbol => stocks.get(symbol)
}

export const useLiveStatus = () => useSyncExternalStore(subscribe, () => status)

/** 'up' / 'down' for ~0.8 s after a value changes (price flash). */
export function useFlash(value: number | null | undefined): 'up' | 'down' | null {
  const prev = useRef(value)
  const [dir, setDir] = useState<'up' | 'down' | null>(null)
  useEffect(() => {
    const p = prev.current
    prev.current = value
    if (p == null || value == null || p === value) return
    setDir(value > p ? 'up' : 'down')
    const t = setTimeout(() => setDir(null), 800)
    return () => clearTimeout(t)
  }, [value])
  return dir
}
